import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { internationalListings } from "../data/international";
import { getIntlDescription } from "../i18n/getIntlDescription";
import { getIntlAmenities }   from "../i18n/getIntlAmenities";
import Lightbox from "../components/shared/Lightbox";
import {
  THB_TO_EUR,
  THB_TO_AED,
  EUR_TO_AED,
  PHOTO_GRID_COLS,
  PHOTOS_PREVIEW_COUNT,
  WA_URL, TG_URL, PHONE_RAW, PHONE_DISPLAY, SITE_URL,
} from "../config/constants";
import "./PropertyDetail.css";
import "./InternationalDetail.css";

export default function InternationalDetail() {
  const { slug } = useParams();
  const [lightbox, setLightbox] = useState(null);
  const { t, i18n } = useTranslation("international");

  const listing = internationalListings.find(l => l.slug === slug);

  // useMemo MUST be called before any early return to satisfy Rules of Hooks.
  // When listing is null both memos return their safe empty defaults.
  const description = useMemo(
    () => listing ? getIntlDescription(listing, i18n.language) : null,
    [listing, i18n.language]
  );
  const amenitiesList = useMemo(
    () => listing ? getIntlAmenities(listing, i18n.language) : [],
    [listing, i18n.language]
  );

  if (!listing) return (
    <main className="pd-notfound container">
      <h1>{t("detail.notFound")}</h1>
      <Link to="/international">{t("detail.back")}</Link>
    </main>
  );

  // `amenities` removed — consumed via `amenitiesList` (translated)
  const { title, subtitle, location, price, currency, beds, baths, sqm, plotSqm,
          type, images } = listing;


  const pageUrl = `https://pronet-realestate.com/international/${slug}`;
  // Always use the English description for meta tags (SEO)
  const metaDesc = listing.description
    ? listing.description.replace(/\n+/g, " ").trim().slice(0, 160)
    : `${type} for sale in ${location}. ${beds} bed, ${baths} bath. ${sqm} m².`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": title,
    "description": metaDesc,
    "url": pageUrl,
    "image": images[0],
    "numberOfRooms": beds,
    "floorSize": { "@type": "QuantitativeValue", "value": sqm, "unitCode": "MTK" },
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": currency,
      "availability": "https://schema.org/InStock"
    },
    "address": { "@type": "PostalAddress", "streetAddress": location, "addressCountry": listing.country },
    "broker": { "@type": "RealEstateAgent", "name": "ProNet Real Estate", "url": SITE_URL, "telephone": PHONE_RAW }
  };

  return (
    <main className="pd">
      <Helmet>
        <title>{title} — {listing.country} | ProNet Real Estate</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={`${title} — ProNet Real Estate`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={images[0]} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {lightbox !== null && (
        <Lightbox
          photos={images}
          initialIndex={lightbox}
          showWatermark
          onClose={() => setLightbox(null)}
        />
      )}

      <div className="pd-hero">
        <div className="intl-badge">{listing.country} · {t("detail.forSale")}</div>
        <h1 className="pd-title">{title}</h1>
        <p className="pd-loc">{location}</p>
      </div>

      <div className="pd-photos" style={{ gridTemplateColumns: images.length >= 2 ? PHOTO_GRID_COLS : "1fr" }}>
        <div className="pd-photo pd-photo-main" onClick={() => setLightbox(0)}>
          <img src={images[0]} alt={title} loading="eager" />
          {images.length > 1 && (
            <div className="pd-photo-hint">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="2" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <rect x="11" y="2" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <rect x="2" y="11" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <rect x="11" y="11" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
              {t("detail.viewAll", { count: images.length })}
            </div>
          )}
        </div>
        {images.length >= 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {images.slice(1, 1 + PHOTOS_PREVIEW_COUNT).map((src, i) => (
              <div key={i} className="pd-photo pd-photo-sm" onClick={() => setLightbox(i + 1)}>
                <img src={src} alt={`${title} ${i + 2}`} loading="lazy" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="container pd-layout">
        <div className="pd-main">
          <div className="pd-breadcrumb">
            <Link to="/international">{t("detail.breadcrumb")}</Link> / {subtitle}
          </div>

          <div className="pd-stats-row">
            <div className="pd-stat"><span className="pd-stat-n">{beds}</span><span className="pd-stat-l">{t("detail.bedrooms")}</span></div>
            <div className="pd-stat"><span className="pd-stat-n">{baths}</span><span className="pd-stat-l">{t("detail.bathrooms")}</span></div>
            <div className="pd-stat"><span className="pd-stat-n">{sqm.toLocaleString()}</span><span className="pd-stat-l">{t("detail.builtArea")}</span></div>
            <div className="pd-stat"><span className="pd-stat-n">{plotSqm.toLocaleString()}</span><span className="pd-stat-l">{t("detail.plotArea")}</span></div>
          </div>

          {description && (
            <div className="pd-sec">
              <h2 className="pd-sec-title">{t("detail.description")}</h2>
              <div className="pd-desc">
                {description.split(/\n+/).map(s => s.trim()).filter(Boolean).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          )}

          {amenitiesList.length > 0 && (
            <div className="pd-sec">
              <h2 className="pd-sec-title">{t("detail.amenities")}</h2>
              <div className="pd-amenities">
                {amenitiesList.map((a) => <span key={a} className="pd-amenity">{a}</span>)}
              </div>
            </div>
          )}

          <div className="pd-sec">
            <h2 className="pd-sec-title">{t("detail.details")}</h2>
            <table className="pd-table"><tbody>
              <tr><td>{t("detail.type")}</td><td>{type}</td></tr>
              <tr><td>{t("detail.status")}</td><td>{t("detail.forSale")}</td></tr>
              <tr><td>{t("detail.location")}</td><td>{location}</td></tr>
              <tr><td>{t("detail.builtAreaLabel")}</td><td>{sqm.toLocaleString()} m²</td></tr>
              <tr><td>{t("detail.plotAreaLabel")}</td><td>{plotSqm.toLocaleString()} m²</td></tr>
            </tbody></table>
          </div>
        </div>

        <aside className="pd-sidebar">
          <div className="pd-price-card">
            <div className="pd-price">
              {new Intl.NumberFormat("de-DE").format(price)} {currency}
            </div>
            {currency === "THB" && (
              <div className="pd-price-alts">
                <span>≈ {new Intl.NumberFormat("de-DE").format(Math.round(price * THB_TO_EUR))} EUR</span>
                <span className="pd-price-alt-sep">·</span>
                <span>≈ {new Intl.NumberFormat("de-DE").format(Math.round(price * THB_TO_AED))} AED</span>
              </div>
            )}
            {currency === "EUR" && (
              <div className="pd-price-alts">
                <span>≈ {new Intl.NumberFormat("de-DE").format(Math.round(price * EUR_TO_AED))} AED</span>
              </div>
            )}
            <p className="pd-price-note">{t("detail.priceIn", { currency })}</p>
            <div className="pd-price-rule" />
            <a href={WA_URL} target="_blank" rel="noreferrer" className="pd-btn pd-btn-wa">{t("detail.whatsapp")}</a>
            <a href={TG_URL} target="_blank" rel="noreferrer" className="pd-btn pd-btn-tg">Telegram</a>
            <a href={`tel:${PHONE_RAW}`} className="pd-btn pd-btn-ph">{PHONE_DISPLAY}</a>
            {listing.pfUrl && (
              <a href={listing.pfUrl} target="_blank" rel="noreferrer" className="pd-btn pd-btn-ext">
                {t("detail.viewSite")}
              </a>
            )}
          </div>
        </aside>
      </div>

      {images.length > 3 && (
        <section className="intl-all-photos">
          <div className="container">
            <h2 className="pd-sec-title" style={{ marginBottom: "2rem" }}>{t("detail.allPhotos")}</h2>
            <div className="intl-photo-grid">
              {images.map((src, i) => (
                <div key={i} className="intl-photo-item" onClick={() => setLightbox(i)}>
                  <img src={src} alt={`${title} ${i + 1}`} loading="lazy" />
                  <div className="intl-photo-wm" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
